/**
 * ScrollToTopButton Component
 * 
 * A floating button that appears when user scrolls down and 
 * smoothly scrolls back to the top when clicked.
 * 
 * Features:
 * - Appears after scrolling 300px
 * - Smooth scroll animation
 * - TitleFlow brand styling
 * - Accessible (keyboard focusable, aria-label)
 * - Throttled scroll listener for performance
 * 
 * @example
 * <ScrollToTopButton />
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowUp } from 'lucide-react';
import { cn } from '../../lib/utils';

// Throttle function for scroll performance
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

const ScrollToTopButton = ({ 
  threshold = 300,      // Scroll distance before button appears
  className,
  containerRef = null   // Optional: scroll container ref (defaults to window)
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Handle scroll visibility check
  const checkScrollPosition = useCallback(() => {
    let scrollY;
    
    if (containerRef?.current) {
      // Scroll within a container
      scrollY = containerRef.current.scrollTop;
    } else {
      // Window scroll
      scrollY = window.scrollY || window.pageYOffset;
    }
    
    setIsVisible(scrollY > threshold);
  }, [threshold, containerRef]);

  // Throttled scroll handler
  useEffect(() => {
    const throttledCheck = throttle(checkScrollPosition, 100);
    
    const scrollTarget = containerRef?.current || window;
    scrollTarget.addEventListener('scroll', throttledCheck, { passive: true });
    
    // Initial check
    checkScrollPosition();
    
    return () => {
      scrollTarget.removeEventListener('scroll', throttledCheck);
    };
  }, [checkScrollPosition, containerRef]);

  // Scroll to top handler
  const scrollToTop = () => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Handle keyboard activation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollToTop();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <button
      onClick={scrollToTop}
      onKeyDown={handleKeyDown}
      className={cn(
        // Position - fixed bottom-right
        "fixed bottom-6 right-6 z-50",
        // Size and shape
        "w-12 h-12 rounded-full",
        // TitleFlow brand color (blue-600)
        "bg-[#2563eb] hover:bg-[#1d4ed8] active:bg-[#1e40af]",
        // Shadow and depth
        "shadow-lg hover:shadow-xl",
        // Transition effects
        "transition-all duration-200 ease-in-out",
        // Hover scale effect
        "hover:scale-110 active:scale-95",
        // Focus states for accessibility
        "focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600",
        // Flex center for icon
        "flex items-center justify-center",
        // Text color
        "text-white",
        // Animation on appear
        "animate-fade-in",
        className
      )}
      aria-label="Back to top"
      title="Back to top"
      tabIndex={0}
      data-testid="scroll-to-top-button"
    >
      <ArrowUp className="w-5 h-5" strokeWidth={2.5} />
    </button>
  );
};

export default ScrollToTopButton;
